import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// routes
import { PATH_DASHBOARD } from 'src/routes/paths';
// components
import ChatBottomBar from 'src/sections/@chats/chat/ChatBottomBar';
import ChatHeader from 'src/sections/@chats/chat/ChatHeader';
import ChatMessageList from './ChatMessageList';
import ChatInfoSidebar from 'src/sections/@chats/chat/ChatInfoSidebar';
// hooks
import useAuth from 'src/hooks/useAuth';
// redux
import { useDispatch, useSelector } from 'src/redux/store';
// actions
import DrawerChatGpt from 'src/components/_chats/chatgpt/DrawerChatgpt';
import v1_actionEnviarGpt from 'src/actions/v1/chatgpt/v1_actionEnviarGpt';
import Iconify from 'src/components/Iconify';
// helpers
import { getMessagesBySubstring } from 'src/helpers/cache.helper';
import { getSentimentAnalysis } from 'src/redux/slices/chat';
import { verifyRules } from 'src/utils/functions';

// ----------------------------------------------------------------------

export default function ChatWindow({
    config,
    chatInfo,
    sidebarLeft,
    changeOpenSidebarLeft,
    source,
    onCloseModal,
    layoutMode,
    hideSidebar = false,
    onSidebarToggle,
}) {
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const { t } = useTranslation('chat');
    const {
        loadingChat,
        mensagens,
        chatIsOnline,
        mensagensEnviar,
        mensagensArquivos,
        chatsAnalysis,
    } = useSelector((state) => state.chat);
    const { isUserStandby } = useSelector((state) => state.user);
    const { onlineUsers } = useSelector((state) => state.staff);
    const { listaUsuariosTransferencia } = useSelector((state) => state.user);
    const { user, cliente } = useAuth();

    const [messagesError, setMessagesError] = useState([]);
    const [drawerChatgpt, setDrawerChatgpt] = useState(false);
    const [respostaGpt, setRespostaGpt] = useState('');
    const [respostasGpt, setRespostasGpt] = useState([]);
    const [loadingGpt, setLoadingGpt] = useState(false);
    const [conversacao, setConversacao] = useState([]);
    const [textInputDrawer, setTextInputDrawer] = useState('');
    const [isShowContextOptions, setIsShowContextOptions] = useState(false);
    const [openSidebar, setOpenSidebar] = useState(true);
    const [showAlert, setShowAlert] = useState(false);
    const mediaOverlayRef = useRef(null);
    // Expoe "pegar chat" / "transferir" do ChatHeader (mesma logica, mesmo
    // loading, mesmo refresh de listas) pro alerta abaixo poder chama-los sem
    // duplicar o fluxo inteiro do modal de transferencia.
    const chatHeaderRef = useRef(null);

    // Constants
    const CHAT_STATUS = {
        ATTENDING: 'Atendendo',
        FINISHED: 'Finalizado',
    };

    // `mensagensEnviar` é global até o echo do socket. Só o chat_id da conversa
    // aberta entra na lista — fallback por telefone misturava contatos iguais em
    // sessões diferentes e balões sem chat_id vazavam para qualquer chat.
    const mensagensEnviarDoChat = useMemo(() => {
        const lista = Array.isArray(mensagensEnviar) ? mensagensEnviar : [];
        if (!chatInfo?._id) return [];
        const chatId = String(chatInfo._id);
        return lista.filter((msg) => msg?.chat_id && String(msg.chat_id) === chatId);
    }, [mensagensEnviar, chatInfo?._id]);

    const messagesErrorDoChat = useMemo(() => {
        const lista = Array.isArray(messagesError) ? messagesError : [];
        if (!chatInfo?._id) return [];
        const chatId = String(chatInfo._id);
        return lista.filter((msg) => {
            if (msg?.chat_id) return String(msg.chat_id) === chatId;
            const id = msg?.id != null ? String(msg.id) : '';
            return id.startsWith(`${chatId}_`);
        });
    }, [messagesError, chatInfo?._id]);

    const messagesMerge = useMemo(() => {
        return mensagens.concat(mensagensEnviarDoChat).concat(messagesErrorDoChat);
    }, [mensagens, mensagensEnviarDoChat, messagesErrorDoChat]);

    const messagesMergeArquivos = useMemo(() => {
        return mensagensArquivos.concat(mensagensEnviarDoChat).reverse();
    }, [mensagensArquivos, mensagensEnviarDoChat]);

    // Functions
    // A conversa aberta vive numa ref porque `onCallbackResend` é chamado de
    // dentro de callbacks assíncronos da barra de envio (o catch de sendMessage,
    // por exemplo). Com a closure antiga, um envio que falhava depois da troca de
    // conversa recarregava os erros da conversa ORIGINAL e os desenhava dentro da
    // conversa aberta — era esse o balão que "aparecia em outra conversa".
    const chatIdRef = useRef(chatInfo?._id);
    useEffect(() => {
        chatIdRef.current = chatInfo?._id;
    }, [chatInfo?._id]);

    const getMessagesError = useCallback(async () => {
        const chatId = chatIdRef.current;
        if (!chatId) {
            setMessagesError([]);
            return;
        }
        const messages_error = await getMessagesBySubstring(String(chatId));
        // Trocou de conversa durante o await: descarta o resultado obsoleto.
        if (chatIdRef.current !== chatId) return;
        setMessagesError(messages_error);
    }, []);

    const handleEnviarGpt = async (mensagens) => {
        setDrawerChatgpt(true);
        setIsShowContextOptions(false);
        const arrayFilter = mensagens.filter((i) => i.type === 'chat');
        const updatedArr = arrayFilter.map((item) => {
            return {
                body: item.body.replace(/\*\_.*?\_\*/g, ''),
                type: item.type,
                fromMe: item.fromMe,
            };
        });

        setLoadingGpt(true);
        try {
            const querys = `?usuario_id=${user.id}&cliente_id=${user.clienteId}`;
            const response_chatgpt = await v1_actionEnviarGpt(updatedArr, querys);
            if (response_chatgpt.data.data.length > 0) {
                const resposta = response_chatgpt.data.data[0].message.content;
                const newObj = {
                    body: resposta,
                    type: 'chat',
                    fromMe: false,
                    fromGpt: true,
                };
                setConversacao((prev) => [...prev, newObj]);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingGpt(false);
        }
    };

    const onStartConversationGpt = async (inputText, isGatilho, nomeGatilho) => {
        try {
            const newObj = { body: inputText, type: 'chat', fromMe: true };
            const newObjPush = { body: nomeGatilho, type: 'chat', fromMe: true };
            setConversacao((prev) => [...prev, newObjPush]);
            setLoadingGpt(true);
            const querys = `?usuario_id=${user.id}&cliente_id=${user.clienteId}&ignore_instruction=1`;
            const response_chatgpt = await v1_actionEnviarGpt([newObj], querys);
            if (response_chatgpt.data.data.length > 0) {
                const resposta = response_chatgpt.data.data[0].message.content;
                const newObjResponse = {
                    body: resposta,
                    type: 'chat',
                    fromMe: false,
                    fromGpt: true,
                };
                setConversacao((prev) => [...prev, newObjResponse]);
                setTextInputDrawer('');
            }
        } catch (error) {
            console.error('onStartConversationGpt error', error);
        } finally {
            setLoadingGpt(false);
        }
    };

    const onStartConversationGptFree = async () => {
        try {
            const newObj = { body: textInputDrawer, type: 'chat', fromMe: true };
            setConversacao((prev) => [...prev, newObj]);
            setLoadingGpt(true);
            const querys = `?usuario_id=${user.id}&cliente_id=${user.clienteId}&ignore_instruction=1`;
            const response_chatgpt = await v1_actionEnviarGpt([newObj], querys);
            if (response_chatgpt.data.data.length > 0) {
                const resposta = response_chatgpt.data.data[0].message.content;
                const newObjResponse = {
                    body: resposta,
                    type: 'chat',
                    fromMe: false,
                    fromGpt: true,
                };
                setConversacao((prev) => [...prev, newObjResponse]);
                setTextInputDrawer('');
            }
        } catch (error) {
            console.error('onStartConversationGptFree error', error);
        } finally {
            setLoadingGpt(false);
        }
    };

    const handleCallbackGpt = async (value) => {
        if (!value) return;
        setDrawerChatgpt(true);
        onStartConversationGpt(
            ('Texto:' + value.message + '. Instrução:' + value.input).replace(/\*\_.*?\_\*|\n/g, ''),
            true,
            value.nome
        );
    };

    // Effects
    // Zera na hora que a conversa muda: durante o await do IndexedDB os erros da
    // conversa anterior continuariam desenhados.
    useEffect(() => {
        setMessagesError([]);
    }, [chatInfo?._id]);

    useEffect(() => {
        getMessagesError();
    }, [chatInfo?._id, mensagensEnviar, getMessagesError]);

    useEffect(() => {
        if (verifyRules(user?.rules || [], 'chats.show_bottomBar') && chatInfo.statusRoom !== CHAT_STATUS.ATTENDING) {
            setShowAlert(true);
        } else {
            setShowAlert(false);
        }
    }, [chatInfo?.statusRoom, user?.rules]);

    useEffect(() => {
        if (source === 'funil.chat') {
            setOpenSidebar(false);
        }
    }, [source]);

    useEffect(() => {
        dispatch(getSentimentAnalysis(chatInfo?._id, user.clienteId));
    }, [chatInfo?._id, dispatch, user.clienteId]);

    const heightClass =
        layoutMode === 'sidebar'
            ? 'h-full'
            : source === 'funil.chat'
                ? 'h-[calc(100vh-150px)] sm:h-[calc(90vh-150px)] lg:h-[calc(90vh-130px)]'
                : source === 'chat.supervisionar'
                    ? 'h-[calc(100vh-185px)]'
                    : 'h-[calc(100vh-125px)]';

    return (
        <div className="flex w-full flex-grow">
            {drawerChatgpt && (
                <DrawerChatGpt
                    open={drawerChatgpt}
                    user={user}
                    onClear={() => {
                        setRespostaGpt('');
                        setRespostasGpt([]);
                        setConversacao([]);
                        setDrawerChatgpt(false);
                    }}
                    loadingGpt={loadingGpt}
                    conversacao={conversacao}
                    mensagens={mensagens}
                    onClose={() => setDrawerChatgpt(false)}
                    resposta={respostaGpt}
                    respostas={respostasGpt}
                    onEnviarGpt={() => handleEnviarGpt(mensagens)}
                    onGerarResposta={handleEnviarGpt}
                    onStartConversationGpt={() => onStartConversationGptFree()}
                    isShowContextOptions={isShowContextOptions}
                    textInputDrawer={textInputDrawer}
                    changeText={(e) => setTextInputDrawer(e.target.value)}
                    choice={(value) => {
                        // callbackSendTextBottomBar(value.body) // Needs reference
                        setDrawerChatgpt(false);
                        setRespostaGpt('');
                    }}
                />
            )}

            <div className="flex flex-row w-full h-full">
                <div ref={mediaOverlayRef} className="relative w-full">
                    {!isUserStandby && (
                        <ChatHeader
                            ref={chatHeaderRef}
                            openSidebar={hideSidebar ? false : openSidebar}
                            sidebarLeft={sidebarLeft}
                            changeOpenSidebarLeft={() => {
                                if (hideSidebar && onSidebarToggle) {
                                    onSidebarToggle();
                                } else {
                                    setOpenSidebar(false);
                                }
                            }}
                            changeOpenSidebar={() => {
                                changeOpenSidebarLeft();
                                if (hideSidebar && onSidebarToggle) {
                                    onSidebarToggle();
                                } else {
                                    setOpenSidebar(!openSidebar);
                                }
                            }}
                            cliente={cliente}
                            user={user}
                            chatIsOnline={chatIsOnline}
                            onlineUsers={onlineUsers}
                            chat={chatInfo}
                            listaUsuariosTransferencia={listaUsuariosTransferencia}
                            source={source}
                            onCloseModal={onCloseModal}
                            mensagens={messagesMerge}
                        />
                    )}

                    <div
                        className={`flex flex-col flex-1 overflow-hidden dark:bg-none bg-gradient-to-br from-[#e2e8f0] to-[#ffffff] ${heightClass}`}
                    >
                        <ChatMessageList
                            source={source}
                            callbackGpt={handleCallbackGpt}
                            config={config}
                            user={user}
                            loadingChat={loadingChat}
                            cliente={cliente}
                            chat={chatInfo}
                            conversa={messagesMerge}
                            mensagens={mensagens}
                            onCallbackResend={getMessagesError}
                            sentimentAnalysis={chatsAnalysis}
                        />

                        <div>
                            {!isUserStandby &&
                                (chatInfo.statusRoom === CHAT_STATUS.ATTENDING || verifyRules(user?.rules || [], 'chats.show_bottomBar')) && (
                                    <>
                                        {/* Alerta estático para chat não em atendimento */}
                                        {showAlert && (
                                            <div className="flex items-center justify-between gap-3 mx-4 my-2 px-4 py-3 rounded bg-[#fff3cd] border border-[#ffeaa7]">
                                                <div className="flex items-center min-w-0">
                                                    <div className="w-2 h-2 rounded-full bg-[#f39c12] mr-3 flex-shrink-0" />
                                                    <span className="font-medium text-[#856404] truncate">{t('header.notInServiceWarning')}</span>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    {/* Mesmos handlers do ChatHeader (via ref): pegar/transferir daqui em
                                                        diante rodam o fluxo real (loading, snackbar, refresh de listas),
                                                        nao uma reimplementacao. */}
                                                    <button
                                                        type="button"
                                                        onClick={() => chatHeaderRef.current?.handleAceitar()}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#856404] hover:opacity-90 text-white text-xs font-medium whitespace-nowrap"
                                                    >
                                                        <Iconify icon="eva:checkmark-circle-2-outline" width={14} />
                                                        {t('header.catchChat')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => chatHeaderRef.current?.handleOpenModalTransferirChat()}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#856404]/40 hover:bg-[#856404]/10 text-[#856404] text-xs font-medium whitespace-nowrap"
                                                    >
                                                        <Iconify icon="eva:swap-outline" width={14} />
                                                        {t('header.transferChat')}
                                                    </button>
                                                    <div onClick={() => setShowAlert(false)} className="cursor-pointer text-lg font-bold text-[#856404] hover:opacity-70 px-1">
                                                        ×
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {chatInfo?.bloqueado && (
                                            <div className="mx-4 mt-3 flex items-start gap-3 px-4 py-3 rounded-xl border border-red-200/70 bg-gradient-to-r from-red-50 to-rose-50/70 shadow-sm shadow-red-500/5 dark:from-red-500/10 dark:to-rose-500/5 dark:border-red-500/30">
                                                <div className="w-9 h-9 rounded-xl bg-red-100 dark:bg-red-500/20 flex items-center justify-center flex-shrink-0">
                                                    <Iconify icon="solar:shield-cross-bold" width={18} className="text-red-600 dark:text-red-400" />
                                                </div>
                                                <div className="flex flex-col min-w-0 leading-tight">
                                                    <span className="text-sm font-semibold text-red-800 dark:text-red-200">
                                                        Contato bloqueado
                                                    </span>
                                                    <span className="text-xs text-red-700/80 dark:text-red-300/80 mt-0.5">
                                                        Não é possível interagir com este contato. Desbloqueie pelo menu do chat para voltar a enviar mensagens.
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        <div
                                            className={chatInfo?.bloqueado ? 'pointer-events-none opacity-50 select-none' : ''}
                                            aria-disabled={chatInfo?.bloqueado || undefined}
                                        >
                                            <ChatBottomBar
                                                layoutMode={layoutMode}
                                                mediaOverlayRef={mediaOverlayRef}
                                                cliente={cliente}
                                                onOpenDrawerGpt={() => {
                                                    setIsShowContextOptions(true);
                                                    setDrawerChatgpt(true);
                                                }}
                                                user={user}
                                                mensagens={messagesMerge}
                                                disabled={pathname === PATH_DASHBOARD.chat.new || !!chatInfo?.bloqueado}
                                                chat={chatInfo}
                                                onCallbackResend={getMessagesError}
                                                onCallbackAnalysis={() => dispatch(getSentimentAnalysis(chatInfo?._id))}
                                            />
                                        </div>
                                    </>
                                )}
                        </div>
                    </div>
                </div>

                {!hideSidebar && openSidebar && (
                    <div
                        className={`overflow-visible bg-background w-[320px] min-w-[320px]`}
                        style={{ width: openSidebar ? '320px' : '0', minWidth: openSidebar ? '320px' : '0' }}
                    >
                        <ChatInfoSidebar source={source} chat={chatInfo} messagesWpp={messagesMergeArquivos} />
                    </div>
                )}
            </div>
        </div>
    );
}
